/**
 * Loyalty Status Component
 * Shows customer loyalty tier, discount, and progress to next tier
 */

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import axios from 'axios';
import { Star, Gift, TrendingUp, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tier colors
const TIER_COLORS = {
  Bronze: 'from-amber-600 to-amber-700',
  Silver: 'from-gray-400 to-gray-500',
  Gold: 'from-yellow-500 to-yellow-600',
  Platinum: 'from-purple-500 to-blue-500'
};

export default function LoyaltyStatus({ compact = false, showOnlyIfMember = false }) {
  const { user, getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [loyalty, setLoyalty] = useState(null);

  useEffect(() => {
    if (user?.user_id) {
      fetchLoyaltyStatus();
    }
  }, [user]);

  const fetchLoyaltyStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/retention/loyalty/${user.user_id}`,
        { headers: getAuthHeaders(), withCredentials: true }
      );
      setLoyalty(response.data);
    } catch (error) {
      console.error('Failed to fetch loyalty status:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return compact ? null : (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-5 h-5 animate-spin" />
      </div>
    );
  }

  if (!loyalty) return null;

  // Don't show if not a member and showOnlyIfMember is true
  if (showOnlyIfMember && !loyalty.has_loyalty) return null;

  // Compact badge view
  if (compact && loyalty.has_loyalty) {
    return (
      <Badge 
        className={`bg-gradient-to-r ${TIER_COLORS[loyalty.tier_name] || TIER_COLORS.Bronze}`}
        data-testid="loyalty-badge"
      >
        {loyalty.badge} {loyalty.tier_name} • {loyalty.discount_percent}% off
      </Badge>
    );
  }

  // Non-member view
  if (!loyalty.has_loyalty) {
    return (
      <Card className="border-dashed border-2" data-testid="loyalty-status">
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
              <Star className="w-5 h-5 text-gray-400" />
            </div>
            <div>
              <p className="font-medium text-gray-900">Unlock Loyalty Rewards</p>
              <p className="text-sm text-gray-500">
                Complete {loyalty.services_to_next_tier} service(s) to earn{' '}
                <span className="text-green-600 font-medium">
                  {loyalty.next_tier.discount_percent}% off
                </span>
              </p>
            </div>
          </div>
          
          {/* Progress */}
          <div className="mt-4">
            <div className="flex justify-between text-xs mb-1">
              <span>Progress to {loyalty.next_tier.name}</span>
              <span>{loyalty.completed_services}/{loyalty.next_tier.min_services}</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all"
                style={{ width: `${(loyalty.completed_services / loyalty.next_tier.min_services) * 100}%` }}
              />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Member view
  return (
    <Card 
      className={`bg-gradient-to-br ${TIER_COLORS[loyalty.tier_name] || 'from-blue-500 to-blue-600'} text-white`}
      data-testid="loyalty-status"
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-4xl">{loyalty.badge}</span>
            <div>
              <p className="font-bold text-lg">{loyalty.tier_name} Member</p>
              <p className="text-white/80 text-sm">
                {loyalty.completed_services} services completed
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold">{loyalty.discount_percent}%</p>
            <p className="text-white/80 text-sm">discount</p>
          </div>
        </div>

        {/* Next tier progress */}
        {!loyalty.is_max_tier && loyalty.next_tier && (
          <div className="mt-4 pt-4 border-t border-white/20">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="flex items-center gap-1">
                <TrendingUp className="w-4 h-4" />
                {loyalty.services_to_next_tier} more to {loyalty.next_tier.name}
              </span>
              <span>{loyalty.next_tier.discount_percent}% discount</span>
            </div>
            <div className="w-full bg-white/30 rounded-full h-2">
              <div 
                className="bg-white h-2 rounded-full transition-all"
                style={{ 
                  width: `${((loyalty.completed_services - (loyalty.next_tier.min_services - loyalty.services_to_next_tier)) / loyalty.services_to_next_tier) * 100}%` 
                }}
              />
            </div>
          </div>
        )}

        {loyalty.is_max_tier && (
          <div className="mt-4 pt-4 border-t border-white/20 text-center">
            <p className="flex items-center justify-center gap-2">
              <Gift className="w-4 h-4" />
              You've reached the highest tier!
            </p>
          </div>
        )}

        {/* Total spent */}
        {loyalty.total_spent > 0 && (
          <p className="text-center text-white/70 text-xs mt-3">
            Total savings to date: ${Math.round(loyalty.total_spent * (loyalty.discount_percent / 100))}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

// Calculate and display loyalty discount on a price
export function LoyaltyDiscount({ originalPrice, onDiscountCalculated }) {
  const { user, getAuthHeaders } = useAuth();
  const [discount, setDiscount] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user?.user_id && originalPrice > 0) {
      calculateDiscount();
    }
  }, [user, originalPrice]);

  const calculateDiscount = async () => {
    try {
      const response = await axios.post(
        `${API}/retention/loyalty/apply-discount`,
        null,
        {
          params: {
            user_id: user.user_id,
            original_price: originalPrice
          },
          headers: getAuthHeaders(),
          withCredentials: true
        }
      );
      setDiscount(response.data);
      if (onDiscountCalculated) {
        onDiscountCalculated(response.data);
      }
    } catch (error) {
      console.error('Failed to calculate discount:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading || !discount) return null;

  if (!discount.discount_applied) {
    return (
      <p className="text-sm text-blue-600">
        💡 {discount.message}
      </p>
    );
  }

  return (
    <div className="bg-green-50 border border-green-200 rounded-lg p-3" data-testid="loyalty-discount">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-lg">{discount.badge}</span>
          <span className="font-medium text-green-800">{discount.tier} Discount</span>
        </div>
        <Badge className="bg-green-600">-{discount.discount_percent}%</Badge>
      </div>
      <div className="flex justify-between mt-2 text-sm">
        <span className="text-gray-600 line-through">${discount.original_price}</span>
        <span className="text-green-600 font-bold">${discount.final_price}</span>
      </div>
      <p className="text-xs text-green-700 mt-1">{discount.message}</p>
    </div>
  );
}
